import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { springLayout } from './motion'

export interface ChipTabOption<T extends string> {
  key: T
  label: ReactNode
}

interface Props<T extends string> {
  options: ChipTabOption<T>[]
  value: T
  onChange: (next: T) => void
  'aria-label': string
  className?: string
}

/**
 * Every choice made inside a page: a feed, a filter, a board, a person.
 *
 * The app has two levels of tabs and only two. The section tabs at the top of
 * the screen are underlined (`features/nav/SectionTabs`). Everything inside a
 * page is this: chips, dark when selected. Never a second underlined strip, a
 * segmented track or HeroUI `Tabs`. One look means a member learns it once.
 *
 * Deliberately not HeroUI `Tabs`. Its selected state paints its own grey
 * (`default-300`) and that beats our classes, so a "dark" chip came out grey
 * with white text, which nobody could read. Here every colour comes from our
 * own tokens.
 *
 * The chips wrap onto a second line instead of scrolling. A row that scrolled
 * hid "Săptămâna aceasta" off the edge with no sign it was there.
 *
 * Arrow keys move between chips, as in any tab list.
 */
export function ChipTabs<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className = '',
}: Props<T>) {
  const indicatorId = useId()
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  const handleKey = (e: KeyboardEvent, index: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = (index + step + options.length) % options.length
    refs.current[next]?.focus()
    onChange(options[next].key)
  }

  return (
    <div role="tablist" aria-label={ariaLabel} className={`flex flex-wrap gap-1.5 ${className}`}>
      {options.map((option, i) => {
        const isActive = option.key === value
        return (
          <button
            key={option.key}
            ref={(el) => {
              refs.current[i] = el
            }}
            role="tab"
            type="button"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(option.key)}
            onKeyDown={(e) => handleKey(e, i)}
            className={`relative flex h-8 items-center rounded-full border px-3.5 text-[13px] font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--gym-primary)] focus-visible:ring-offset-2 ${
              isActive
                ? 'border-foreground'
                : 'border-[var(--gym-line)] bg-content1 hover:border-foreground/30'
            }`}
          >
            {isActive && (
              <motion.span
                layoutId={indicatorId}
                transition={springLayout}
                aria-hidden
                className="absolute inset-0 rounded-full bg-foreground"
              />
            )}
            <span
              className={`relative z-10 flex items-center gap-1.5 whitespace-nowrap ${
                isActive ? 'text-background' : 'text-foreground/70'
              }`}
            >
              {option.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}
